import { createTheme, type Theme } from "@mui/material/styles";

/**
 * Design system tokens per DESIGN.md.
 *
 * The app should read like a field instrument, not a SaaS dashboard:
 *   - Paper + ink palette with a single warm orange accent
 *   - Serif for editorial display, Inter for UI, JetBrains Mono for numerals
 *   - Hairline rules instead of boxed cards where possible
 *   - 4px radius, no shadows, no gradients, no emojis
 */

export const designTokens = {
  light: {
    paper: "#F6F1E7",
    paperRaised: "#FFFBF2",
    ink: "#0E1B26",
    inkMuted: "#4A5A68",
    rule: "rgba(26, 42, 54, 0.12)",
    ruleStrong: "rgba(26, 42, 54, 0.24)",
    accent: "#E5541B",
    accentDeep: "#B33B0D",
    kelp: "#2E5D4A",
    buoy: "#C8331F",
    chartSwell: "#0E1B26",
    chartWind: "#8A6B3D",
  },
  dark: {
    paper: "#06080B",
    paperRaised: "#0E141B",
    ink: "#EDE6D6",
    inkMuted: "#8A95A1",
    rule: "rgba(237, 230, 214, 0.1)",
    ruleStrong: "rgba(237, 230, 214, 0.2)",
    accent: "#FF6A2C",
    accentDeep: "#E5541B",
    kelp: "#5B9C82",
    buoy: "#E5644F",
    chartSwell: "#EDE6D6",
    chartWind: "#C7A879",
  },
} as const;

declare module "@mui/material/styles" {
  interface Palette {
    ink: { main: string; muted: string };
    paper: { raised: string };
    accent: { main: string; deep: string };
    kelp: { main: string };
    buoy: { main: string };
    rule: { main: string; strong: string };
    chart: { swell: string; wind: string };
  }
  interface PaletteOptions {
    ink?: { main: string; muted: string };
    paper?: { raised: string };
    accent?: { main: string; deep: string };
    kelp?: { main: string };
    buoy?: { main: string };
    rule?: { main: string; strong: string };
    chart?: { swell: string; wind: string };
  }
}

const SERIF_STACK = `"Source Serif 4", "GT Sectra", "Tiempos Headline", Georgia, serif`;
const SANS_STACK = `"Inter", "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
const MONO_STACK = `"JetBrains Mono", "IBM Plex Mono", "SF Mono", Menlo, monospace`;

export const fontStack = {
  serif: SERIF_STACK,
  sans: SANS_STACK,
  mono: MONO_STACK,
};

function buildTheme(mode: "light" | "dark"): Theme {
  const t = mode === "light" ? designTokens.light : designTokens.dark;

  return createTheme({
    palette: {
      mode,
      primary: { main: t.accent, dark: t.accentDeep, contrastText: t.paper },
      secondary: { main: t.kelp },
      error: { main: t.buoy },
      background: { default: t.paper, paper: t.paperRaised },
      text: { primary: t.ink, secondary: t.inkMuted },
      divider: t.rule,
      ink: { main: t.ink, muted: t.inkMuted },
      paper: { raised: t.paperRaised },
      accent: { main: t.accent, deep: t.accentDeep },
      kelp: { main: t.kelp },
      buoy: { main: t.buoy },
      rule: { main: t.rule, strong: t.ruleStrong },
      chart: { swell: t.chartSwell, wind: t.chartWind },
    },
    typography: {
      fontFamily: SANS_STACK,
      // Display (serif) used for spot names, section titles, card headers.
      h1: {
        fontFamily: SERIF_STACK,
        fontWeight: 500,
        letterSpacing: "-0.02em",
        fontSize: "3rem",
        lineHeight: 1.08,
      },
      h2: {
        fontFamily: SERIF_STACK,
        fontWeight: 500,
        letterSpacing: "-0.02em",
        fontSize: "2rem",
        lineHeight: 1.12,
      },
      h3: {
        fontFamily: SERIF_STACK,
        fontWeight: 500,
        letterSpacing: "-0.01em",
        fontSize: "1.375rem",
        lineHeight: 1.27,
      },
      h4: {
        fontFamily: SERIF_STACK,
        fontWeight: 500,
        letterSpacing: "-0.01em",
        fontSize: "1.125rem",
        lineHeight: 1.33,
      },
      h5: { fontFamily: SERIF_STACK, fontWeight: 500, fontSize: "1rem", lineHeight: 1.4 },
      h6: {
        fontFamily: SERIF_STACK,
        fontWeight: 500,
        fontSize: "0.9375rem",
        lineHeight: 1.4,
      },
      body1: { fontSize: "1rem", lineHeight: 1.5 },
      body2: { fontSize: "0.875rem", lineHeight: 1.42 },
      button: { fontWeight: 500, letterSpacing: 0, textTransform: "none" },
      // "Label" style (uppercase, tracked) used across the app for axis/column/readout labels.
      overline: {
        fontFamily: SANS_STACK,
        fontSize: "0.75rem",
        lineHeight: 1.33,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 4,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.paper,
            color: t.ink,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            fontVariantNumeric: "tabular-nums",
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 4,
            boxShadow: "none",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: t.paperRaised,
            border: "none",
            borderRadius: 4,
            boxShadow: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 4,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
          containedPrimary: {
            backgroundColor: t.accent,
            color: t.paper,
            "&:hover": { backgroundColor: t.accentDeep },
          },
          outlined: {
            borderColor: t.ink,
            color: t.ink,
            "&:hover": { borderColor: t.ink, backgroundColor: "transparent" },
          },
          text: {
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "default" },
        styleOverrides: {
          root: {
            backgroundColor: t.paper,
            backgroundImage: "none",
            borderBottom: `1px solid ${t.rule}`,
            color: t.ink,
            boxShadow: "none",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: t.rule },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: { borderRadius: 4, fontSize: "0.875rem" },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 4 },
        },
      },
    },
  });
}

const lightTheme = buildTheme("light");
export const darkTheme = buildTheme("dark");
export default lightTheme;
